import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../../store/store';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import { showToast } from '../../../../common/Notification';

interface CompanyStats {
  'COPROPRIETES IDENTIFIEES': string;
  'NB DE LOTS': string;
  'NB LOTS HABITATIONS, BUREAUX ET COMMERCES': string;
  'NB DE LOGEMENTS': string;
  'NB DE PARKINGS': string;
}

const KPIs: React.FC = () => {
  const [data, setData] = useState<CompanyStats | null>(null);
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  // function to format with spaces between thousands without decimals
  const formatNumber = (value: number | string) => value.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ' ');

  useEffect(() => {
    if (company && company.id && jwt) {
      fetch(`/api/companies/${company.id}/stats`, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((response) => {
          const keysNeeded = ['nbCopros', 'nbLots', 'nbLotsHBR', 'nbLogements', 'nbParkings'];
          const finalNames = ['COPROPRIETES IDENTIFIEES', 'NB DE LOTS', 'NB LOTS HABITATIONS, BUREAUX ET COMMERCES', 'NB DE LOGEMENTS', 'NB DE PARKINGS'];
          const formattedData = keysNeeded.reduce((acc: CompanyStats, key, index) => {
            acc[finalNames[index] as keyof CompanyStats] = formatNumber(response[key] as number | string);
            return acc;
          }, {} as CompanyStats);
          setData(formattedData);
        })
        .catch((error) => {
          console.error('Error fetching data :', error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
        });
    }
  }, [company, jwt]);

  const handleBookDemo = () => {
    window.open('https://calendly.com/jacques-chauvel-rgpd/croissance-presentation?preview_source=et_card', '_blank');
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div>
        <div className="bg-rgpd-blue px-4 py-4 text-center text-white">
          <h2 className="text-sm font-semibold tracking-wider uppercase">
            Syndic
            {' '}
            {company && company.name ? company.name.toUpperCase() : ''}
            {' '}
            : Votre parc de copropriétés
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-6 px-4 py-8 text-center md:grid-cols-5">
          <div className="flex flex-col items-center">
            <p className="text-3xl font-bold text-gray-900 md:text-4xl">{(data && data['COPROPRIETES IDENTIFIEES']) || <Skeleton height={30} width={60} />}</p>
            <p className="mt-1 text-xs tracking-wide text-gray-500 uppercase">Copropriétés identifiées</p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-3xl font-bold text-gray-900 md:text-4xl">{(data && data['NB DE LOTS']) || <Skeleton height={30} width={120} />}</p>
            <p className="mt-1 text-xs tracking-wide text-gray-500 uppercase">Nb de lots</p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-3xl font-bold text-gray-900 md:text-4xl">{(data && data['NB LOTS HABITATIONS, BUREAUX ET COMMERCES']) || <Skeleton height={30} width={120} />}</p>
            <p className="mt-1 text-xs tracking-wide text-gray-500 uppercase">Nb lots hab., bureaux et commerces</p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-3xl font-bold text-gray-900 md:text-4xl">{(data && data['NB DE LOGEMENTS']) || <Skeleton height={30} width={120} />}</p>
            <p className="mt-1 text-xs tracking-wide text-gray-500 uppercase">Nb de logements</p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-3xl font-bold text-gray-900 md:text-4xl">{(data && data['NB DE PARKINGS']) || <Skeleton height={30} width={80} />}</p>
            <p className="mt-1 text-xs tracking-wide text-gray-500 uppercase">Nb de parkings</p>
          </div>
        </div>
        <div className="flex flex-col gap-4 px-4 pt-2 pb-6">
          <div className="rounded-xl border border-orange-300 bg-orange-50 px-7 py-5 text-center text-base leading-relaxed text-orange-900">
            À ce stade, ces chiffres ne portent que sur vos copropriétés immatriculées au
            {' '}
            <strong className="font-bold text-orange-950">RNIC</strong>
            {' '}
            et sont fiables à
            {' '}
            <strong className="font-bold text-orange-950">environ 70 %</strong>
            , car ils s'appuient sur les adresses sans tenir compte des parcelles cadastrales. La validation des parcelles dans le
            {' '}
            <strong className="font-bold text-orange-950">module Croissance</strong>
            {' '}
            les fiabilisera totalement — mais cet aperçu reflète déjà
            {' '}
            <strong className="font-bold text-orange-950">70 % du potentiel de votre parc</strong>
            .
          </div>
          <div className="rounded-xl bg-rgpd-blue px-7 py-7 text-center text-white">
            <p className="mx-auto mb-5 max-w-2xl text-[17px] leading-relaxed font-medium">
              Pour accéder à vos chiffres copropriété par copropriété et découvrir le module Croissance, prenez rendez-vous en visioconférence.
            </p>
            <button
              onClick={handleBookDemo}
              className="inline-block rounded-full bg-orange-500 px-8 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600"
            >
              Réserver ma visio de 30 minutes
            </button>
            <span className="mt-3 block text-[13px] font-normal text-blue-200">Gratuit · 30 minutes · sans engagement</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIs;
